import * as R from 'ramda'
import { useRef, useEffect, useMemo, useState } from 'react'
import { unstable_batchedUpdates as batch } from 'react-dom'
import { useSelector } from 'react-redux'
import debounce from 'lodash.debounce'
import { useConditionalHook } from '@weblite/use-conditional-hook'
import { Hall, InviteLink, InviteLinkUsage } from '@weblite/ts-action/hall'
import { decodeHallPermission } from '@weblite/permission'
import { RootState } from '$setup/redux'
import { createUseEntity } from '$func/create-use-entity.react-hook'
import { useAsyncFunction } from '$func/async-function.react-hook'
import {
  getAllInviteLinks,
  getJoinedViaLinkMembers,
  searchInJoinedViaLinkMembers,
} from '$logic/hall/invite-link.service'
import { useSearchedUserByIds } from '$logic/users'
import { getHall, isRoomMookeb } from './hall.service'
import { getRoom, useRoom } from '$logic/room'
import hostConf from '$config/frontend'
import { HallInviteLink } from './types'
import { hallInviteLinkStorage } from './invite-link.storage'
import { getUserSessionsLimitFromHallTags } from './hall.service'
import {
  COURSE_VITRINE_TAG,
  VOD_ACTIVATED_TAG,
  HIDE_MEMBER_COUNT_TAG,
  ISSUE_TAG,
} from './constant'

const getHallsView = (state: RootState): Record<string, Hall> =>
  state.main.halls

const getHallView = (roomId: string) => (state: RootState) =>
  getHallsView(state)[roomId]

export const useHall = createUseEntity<Hall>(getHallView, getHall)

export function useHalls() {
  return useSelector(getHallsView)
}

const memoizedSelector = R.memoizeWith(
  hallId => hallId as string,
  hallId => (state: RootState) =>
    (Object.values(state.main.hallInviteLinks) as HallInviteLink[]).filter(
      inviteLink => inviteLink.roomId === hallId,
    ),
)

const loadingInviteLinksHallIds = new Set<string>()
export function useHallInvitationLinks(hallId: string) {
  const unmountedRef = useRef(false)

  const [loading, setLoading] = useState(true)

  const items = useSelector(memoizedSelector(hallId)) as HallInviteLink[]

  useEffect(() => {
    const currentHallId = hallId
    const unMounted = unmountedRef.current

    if (!loadingInviteLinksHallIds.has(currentHallId)) {
      loadingInviteLinksHallIds.add(currentHallId)
      getAllInviteLinks(hallId)
        .then(res => {
          if (res) {
            hallInviteLinkStorage.add(res)
            loadingInviteLinksHallIds.delete(currentHallId)
          }
          if (unMounted) return
          setLoading(false)
        })
        .catch(() => {
          loadingInviteLinksHallIds.delete(currentHallId)
          if (unMounted) return
          setLoading(false)
        })
    }

    return () => {
      unmountedRef.current = true
    }
  }, [hallId])

  return {
    items,
    loading: loading && items?.length === 0,
  }
}

export function useGroupChangeablePermissionsCount(hallId: string) {
  const { entity: hall, loading } = useHall(hallId)
  const decodedPermissions = useMemo(() => {
    if (!hall?.permissions)
      return {
        addMembers: false,
        changeHallInfo: false,
        sendMedia: false,
        sendMessage: false,
        sendWapp: false,
        pinMessage: false,
      }
    const hallPermissions = decodeHallPermission(hall.permissions)
    return R.pick(
      [
        'addMembers',
        'changeHallInfo',
        'sendMedia',
        'sendMessage',
        'sendWapp',
        'pinMessage',
      ],
      hallPermissions,
    )
  }, [hallId, hall?.permissions])

  const permissionsValues = decodedPermissions
    ? R.values(decodedPermissions)
    : []

  const allowedPermissions = R.filter(item => item, permissionsValues)

  return {
    allowedPermissionsCount: allowedPermissions.length,
    permissionsCount: permissionsValues.length,
    loading,
  }
}

export function useJoinedViaLinkMembers(link: InviteLink) {
  const [items, setItems] = useState<InviteLinkUsage[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [hasMoreItemToLoad, setHasMoreItemToLoad] = useState<boolean>(true)
  const [pageToken, setPageToken] = useState<string>()

  const loadMore = async () => {
    const res = await getJoinedViaLinkMembers(link.roomId, link.link, pageToken)
    batch(() => {
      setLoading(false)
      if (!res?.pageToken) setHasMoreItemToLoad(false)
      setPageToken(res?.pageToken)
      setItems([...items, ...(res?.usages || [])])
    })
  }

  return { items, hasMoreItemToLoad, loadMore, loading }
}

const DEBOUNCE_TIME = 700
const debouncedSearchInJoinedViaLinkMembers = debounce(
  searchInJoinedViaLinkMembers,
  DEBOUNCE_TIME,
)

export function useSearchedJoinedViaLinkMembers({
  inviteLink,
  query,
  fetchedItems,
  hasMoreItemToLoad,
}: {
  inviteLink: InviteLink
  query: string
  fetchedItems: InviteLinkUsage[]
  hasMoreItemToLoad: boolean
}) {
  const [items, setItems] = useState<InviteLinkUsage[]>([])
  const [loading, setLoading] = useState<boolean>(false)

  useEffect(() => {
    if (hasMoreItemToLoad) {
      setLoading(true)
      debouncedSearchInJoinedViaLinkMembers(
        inviteLink.roomId,
        inviteLink.link,
        query,
      )?.then(res => {
        setItems(res?.usages || [])
        setLoading(false)
      })
    }
  }, [inviteLink, query, hasMoreItemToLoad])

  const { searchedItems: localResult } = useSearchedUserByIds({
    query,
    userIds: fetchedItems.map(item => item.userId),
  })

  return {
    loading,
    searchedItems: loading
      ? localResult.map(id => fetchedItems.find(item => item.userId === id))
      : items,
  }
}

export function useRoomIsMookeb(roomId: string) {
  const { value, loading } = useAsyncFunction(
    async () => isRoomMookeb(roomId),
    [roomId],
  )

  return { roomIsMookeb: value, loading }
}

export const canRoomHaveCourseVitrine = async (roomId: string) => {
  if (!hostConf.HAS_COURSE_VITRINE) return false

  const room = await getRoom(roomId)
  if (room?.type !== 'CHANNEL') return false

  const hall = await getHall(roomId)
  return Boolean(hall?.tags?.includes(COURSE_VITRINE_TAG))
}

export function useRoomHasCourseVitrine(roomId: string) {
  const room = useRoom(roomId)
  const { entity: hall } =
    useConditionalHook(room?.type === 'CHANNEL', {
      hook: useHall,
      hookArgs: roomId,
      defaultValue: undefined,
    }) || {}
  if (!hostConf.HAS_COURSE_VITRINE) return false

  return room?.type !== 'CHANNEL'
    ? false
    : Boolean(hall?.tags?.includes(COURSE_VITRINE_TAG))
}

export const isVodActivatedInRoom = async (
  roomId: string,
): Promise<boolean> => {
  const room = await getRoom(roomId)
  if (room?.type !== 'CHANNEL') return false

  const hall = await getHall(roomId)
  return !!hall?.tags?.includes(VOD_ACTIVATED_TAG)
}

export function useIsVodActivatedInRoom(roomId: string) {
  const room = useRoom(roomId)

  const { entity: hall } =
    useConditionalHook(room?.type === 'CHANNEL', {
      hook: useHall,
      hookArgs: roomId,
      defaultValue: undefined,
    }) || {}

  return !!hall?.tags?.includes(VOD_ACTIVATED_TAG)
}

const defaultHallTags = [] as string[]

export function useUserSessionsLimit(roomId: string) {
  const { entity: hall } = useHall(roomId)
  const hallTags = hall?.tags ?? defaultHallTags

  const { value, loading } = useAsyncFunction(
    async () => getUserSessionsLimitFromHallTags(hallTags),
    [hallTags],
  )

  return { value, loading }
}

export function useHideMemberCount(roomId: string) {
  const room = useRoom(roomId)

  const { entity: hall } =
    useConditionalHook(room?.type === 'CHANNEL', {
      hook: useHall,
      hookArgs: roomId,
      defaultValue: undefined,
    }) || {}

  return !!hall?.tags?.includes(HIDE_MEMBER_COUNT_TAG)
}

export const canRoomHaveIssue = async (roomId: string) => {
  const room = await getRoom(roomId)
  if (room?.type !== 'CHANNEL') return false

  const hall = await getHall(roomId)
  return Boolean(hall?.tags?.includes(ISSUE_TAG))
}

export function useRoomHasIssue(roomId: string) {
  const room = useRoom(roomId)

  const { entity: hall, loading } =
    useConditionalHook(room?.type === 'CHANNEL', {
      hook: useHall,
      hookArgs: roomId,
      defaultValue: undefined,
    }) || {}

  return Boolean(hall?.tags?.includes(ISSUE_TAG))
}
